import styled from "styled-components/macro";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Typography,
} from "@mui/material";
import { Download, MenuBook } from "@mui/icons-material";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import ReactGA from "react-ga4";
import { useState } from "react";

/**
 * GetKioskPage component provides links to download the Kiosk app and view the Kiosk SOP
 */
function GetKioskPage() {
  const { translate } = useFlightTranslation();
  const [buttonClicks, setButtonClicks] = useState<Record<string, boolean>>({});

  /**
   * Track button click in Google Analytics and send notification to Slack
   * @param buttonName Name of the button that was clicked
   * @param url URL that was clicked
   */
  const trackButtonClick = async (buttonName: string, url: string) => {
    // Track in Google Analytics
    ReactGA.event({
      category: "User",
      action: `Downloaded Kiosk file: ${buttonName}`,
      label: url,
    });

    // Only send to Slack once per button per session
    if (!buttonClicks[buttonName]) {
      setButtonClicks(prev => ({ ...prev, [buttonName]: true }));
    }
  };

  return (
    <StyledGetKioskPage>
      <Card className="kiosk-card">
        <CardContent>
          <Typography variant="h5" component="h1" gutterBottom>
            {translate("Kiosk App Resources")}
          </Typography>

          <div className="button-container">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography variant="h6" component="h2">
                {translate("Windows")}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<MenuBook />}
                href="https://kiosk.raven.ai/Raven+Kiosk+App_SOP+for+Windows.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="kiosk-button"
                onClick={() => {
                  trackButtonClick(
                    "Windows SOP",
                    "https://kiosk.raven.ai/Raven+Kiosk+App_SOP+for+Windows.pdf"
                  );
                }}
              >
                {translate("Kiosk SOP (Windows)")}
              </Button>

              <Button
                variant="contained"
                color="primary"
                startIcon={<Download />}
                href="https://kiosk.raven.ai/Raven.Kiosk_0.6.0_x64_en-US_signed.msi"
                target="_blank"
                rel="noopener noreferrer"
                className="kiosk-button"
                onClick={() => {
                  trackButtonClick(
                    "Windows App MSI",
                    "https://kiosk.raven.ai/Raven.Kiosk_0.6.0_x64_en-US_signed.msi"
                  );
                }}
              >
                {translate("Download Kiosk App (.msi)")}
              </Button>
            </Box>
            <Divider />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography variant="h6" component="h2">
                {translate("Android")}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<MenuBook />}
                href="https://kiosk.raven.ai/Raven+Kiosk+App_SOP+for+Android.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="kiosk-button"
                onClick={() => {
                  trackButtonClick(
                    "Android SOP",
                    "https://kiosk.raven.ai/Raven+Kiosk+App_SOP+for+Android.pdf"
                  );
                }}
              >
                {translate("Kiosk SOP (Android)")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Download />}
                href="https://kiosk.raven.ai/app-universal-release.apk"
                target="_blank"
                rel="noopener noreferrer"
                className="kiosk-button"
                onClick={() => {
                  trackButtonClick(
                    "Android App APK",
                    "https://kiosk.raven.ai/app-universal-release.apk"
                  );
                }}
              >
                {translate("Download Kiosk App (.apk)")}
              </Button>
            </Box>
          </div>
        </CardContent>
      </Card>
    </StyledGetKioskPage>
  );
}

const StyledGetKioskPage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  padding: 2rem;

  .kiosk-card {
    max-width: 500px;
    width: 100%;
  }

  .button-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 24px;
  }

  .kiosk-button {
    width: 100%;
    padding: 12px;
    text-transform: none;
  }
  a {
    color: white;
  }
`;

export default GetKioskPage;
